import { useEffect, useRef, useState } from "react";

import type { Dayjs } from "dayjs";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { fetchPlace } from "@/apis/place";
import { fetchReservation } from "@/apis/reservation";
import CompactDatePicker from "@/components/CompactDatePicker";
import { PlacesByFloor, type SelectedPlace } from "@/dummy/places";
import PlaceInfoCard from "@/pages/Reservation/components/PlaceInfoCard";
import PlacePicker from "@/pages/Reservation/components/PlacePicker";
import TimeTablePicker from "@/pages/Reservation/components/ReservationTab/PlaceSearchTab/TimeTablePicker";
import ReservationTabLayout from "@/pages/Reservation/components/ReservationTab/ReservationTabLayout";
import useCalendarStore from "@/store/calendarStore";
import { Place } from "@/types/place";

const PlaceSearchTab = () => {
  const [placeList, setPlaceList] = useState<PlacesByFloor[]>([]);
  const [selectedBldCd, setSelectedBldCd] = useState("PTK_PTK");
  const [reservationList, setReservationList] = useState([]);
  const { control, getValues, reset, watch, resetField } = useFormContext();
  useWatch({ name: "place" });
  const resetFirstDayOfMonth = useCalendarStore(
    state => state.resetFirstDayOfMonth,
  );

  const placeInfoRef = useRef<HTMLDivElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);

  const timeSelectErrorMessage = getValues("place")
    ? ""
    : "*날짜와 장소를 선택한 후 시간 선택이 가능합니다.";

  useEffect(() => {
    getPlaceList(selectedBldCd);
  }, [selectedBldCd]);

  useEffect(() => {
    reset();
    resetFirstDayOfMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPlaceList = async (bldCd: string) => {
    const res = await fetchPlace(bldCd);
    setPlaceList(res);
  };

  useEffect(() => {
    const date = getValues("date");
    const place = getValues("place");

    if (date && place) {
      getReservationList(date, place);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("date"), watch("place")]);

  const getReservationList = async (date: Dayjs, place: Place) => {
    const res = await fetchReservation(date.format("YYYYMMDD"), place.plcCd);
    setReservationList(res);
  };

  const selectedPlace = getValues("place");

  return (
    <ReservationTabLayout>
      <ReservationTabLayout.Left title="날짜 선택">
        <Controller
          name="date"
          rules={{ required: true }}
          control={control}
          render={({ field: { value, onChange } }) => {
            const handleChangeDate = (date: Dayjs) => {
              onChange(date);
              resetField("place");
              resetField("time");
              resetField("purpose");
            };

            return (
              <CompactDatePicker
                selectedDate={value}
                onSelectDate={handleChangeDate}
              />
            );
          }}
        />
      </ReservationTabLayout.Left>
      <ReservationTabLayout.Right title="장소 선택">
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            className={`px-3 py-1.5 rounded-lg text-small font-medium transition-colors duration-150 ${selectedBldCd === "PTK_PTK" ? "bg-primary text-white shadow-sm" : "bg-gray-light text-gray-dark hover:bg-gray-tinted"}`}
            onClick={() => {
              setSelectedBldCd("PTK_PTK");
              resetField("place");
            }}
          >
            본관
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 rounded-lg text-small font-medium transition-colors duration-150 ${selectedBldCd === "PTK_PMC" ? "bg-primary text-white shadow-sm" : "bg-gray-light text-gray-dark hover:bg-gray-tinted"}`}
            onClick={() => {
              setSelectedBldCd("PTK_PMC");
              resetField("place");
            }}
          >
            선교관
          </button>
        </div>
        <Controller
          name="place"
          rules={{ required: true }}
          control={control}
          render={({ field: { value, onChange } }) => {
            const handleChangePlace = (place: SelectedPlace) => {
              onChange(place);
              resetField("time");
              resetField("purpose");
              setTimeout(() => {
                if (placeInfoRef.current) {
                  const y = placeInfoRef.current.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }, 100);
            };

            return (
              <PlacePicker
                placesByFloor={placeList}
                selectedPlace={value}
                onChange={handleChangePlace}
              />
            );
          }}
        />
      </ReservationTabLayout.Right>

      {/* 장소 정보 섹션 — 장소 선택 시에만 표시 */}
      {selectedPlace && (
        <ReservationTabLayout.Middle title="장소 정보">
          <div ref={placeInfoRef}>
            <PlaceInfoCard plcCd={selectedPlace.plcCd} />
          </div>
        </ReservationTabLayout.Middle>
      )}

      <ReservationTabLayout.Bottom
        title="예약 시간 선택"
        errorMessage={timeSelectErrorMessage}
        isLabel
      >
        {selectedPlace && (
          <div ref={timePickerRef}>
            <Controller
              name="time"
              rules={{ required: true }}
              control={control}
              render={({ field: { value, onChange } }) => {
                const handleTimeChange = (newTimes: number[]) => {
                  onChange(newTimes);
                  if (newTimes.length === 1) {
                    setTimeout(() => {
                      timePickerRef.current?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }
                };

                return (
                  <TimeTablePicker
                    selectedDate={getValues("date")}
                    reservationList={reservationList}
                    selectedTimes={value}
                    onChange={handleTimeChange}
                  />
                );
              }}
            />
          </div>
        )}
      </ReservationTabLayout.Bottom>
    </ReservationTabLayout>
  );
};

export default PlaceSearchTab;
